import React, { forwardRef, useEffect, useRef, useState } from "react";
import AddBoxIcon from "@mui/icons-material/AddBox";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import RemoveIcon from "@mui/icons-material/Remove";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import MenuIcon from "@mui/icons-material/Menu";
import ContentCopy from "@mui/icons-material/ContentCopy";
import {
  Alert,
  AppBar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Snackbar,
  TextField,
  ThemeProvider,
  Toolbar,
  Typography,
  createTheme,
  Drawer,
  Box,
  CircularProgress,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import DeleteIcon from "@mui/icons-material/Delete";
import AppBarCommon from "../AppBarCommon";
import "./Devices.css";
import { copyTextToClipboard } from "../../utils/helper";
import MaterialTable, { MTableBodyRow } from "@material-table/core";
import { styled } from "@mui/material/styles";
import zIndex from "@mui/material/styles/zIndex";

const Devices = () => {
  const defaultMaterialTheme = createTheme();
  const tableRef = useRef();
  const drawerWidth = 260;
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [devices, setDevices] = useState([]);
  const [snackbarMessage, setSnackBarMessage] = useState({
    color: "",
    severity: "",
    message: "",
  });
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editDeviceId, setEditDeviceId] = useState("");
  const [tempDisplayName, setTempDisplayName] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceMetrics, setDeviceMetrics] = useState(null);
  const [isMetricsLoading, setIsMetricsLoading] = useState(false);
  let url;
  const axiosPrivate = useAxiosPrivate();
  const controller = new AbortController();

  useEffect(() => {
    // Only call getDevices if we are not actively typing (handled defensively later) or on mount
    getDevices(searchTerm);
  }, []);

  const getDevices = async (search = searchTerm) => {
    try {
      const url = search 
        ? `/devices/v1?page=1&limit=10&search=${encodeURIComponent(search)}`
        : `/devices/v1?page=1&limit=10`;
      const deviceResponse = await axiosPrivate.get(
        url,
        {
          signal: controller.signal,
        },
      );
      setDevices(deviceResponse.data.devices);
      return deviceResponse.data;
    } catch (err) {
      console.log(err);
    }
  };

  const handleApprove = async (rowData) => {
    const { device_id, device_name, display_name } = rowData;
    try {
      setIsLoading(true);
      await axiosPrivate.post("/devices/v1/approve", { device_id });
      setSnackBarMessage({
        message: `Device ${display_name || device_name} approved successfully`,
        color: "#3cb371",
        severity: "success",
      });
      getDevices();
    } catch (err) {
      setSnackBarMessage({
        message: err.response?.data?.message || "Failed to approve device",
        color: "#d32f2f",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevoke = async (rowData) => {
    const { device_id, device_name, display_name } = rowData;
    try {
      setIsLoading(true);
      await axiosPrivate.post("/devices/v1/revoke", { device_id });
      setSnackBarMessage({
        message: `Device ${display_name || device_name} revoked`,
        color: "#ed6c02",
        severity: "warning",
      });
      getDevices();
    } catch (err) {
      setSnackBarMessage({
        message: err.response?.data?.message || "Failed to revoke device",
        color: "#d32f2f",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveName = async () => {
    try {
      setIsLoading(true);
      await axiosPrivate.post("/devices/v1/update-name", {
        device_id: editDeviceId,
        display_name: tempDisplayName,
      });
      setSnackBarMessage({
        message: "Device name updated successfully",
        color: "#3cb371",
        severity: "success",
      });
      setIsEditDialogOpen(false);
      getDevices();
    } catch (err) {
      setSnackBarMessage({
        message: err.response?.data?.message || "Failed to update device name",
        color: "#d32f2f",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      getDevices(searchTerm);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const fetchMetrics = async (deviceId) => {
    try {
      setIsMetricsLoading(true);
      const res = await axiosPrivate.get(`/devices/v1/${deviceId}/metrics`);
      setDeviceMetrics(res.data);
    } catch (err) {
      setSnackBarMessage({
        message: "Failed to load device metrics",
        color: "#d32f2f",
        severity: "error",
      });
    } finally {
      setIsMetricsLoading(false);
    }
  };

  const handleRowClick = (event, rowData) => {
    setSelectedDevice(rowData);
    setDrawerOpen(true);
    fetchMetrics(rowData.device_id);
  };

  // const StyledMTableBodyRow = styled(MTableBodyRow)({
  //   borderBottomStyle: 'none',
  //   overflow: 'visible',
  //   height: '100%',
  // });

  // const Row = (props) => {
  //   return <StyledMTableBodyRow {...props} />;
  // };

  const columns = [
    {
      title: "Device Name",
      field: "device_name",
      render: (rowData) => {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                backgroundColor: rowData.is_online ? "#3cb371" : "#cfd8dc",
                boxShadow: rowData.is_online ? "0 0 5px rgba(60, 179, 113, 0.6)" : "none",
                flexShrink: 0,
              }}
              title={rowData.is_online ? "Online" : "Offline"}
            />
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span
                style={{
                  fontWeight: rowData.display_name ? "600" : "500",
                  textTransform: "capitalize",
                }}
              >
                {rowData.display_name || rowData.device_name}
              </span>
              {rowData.display_name && (
                <span
                  style={{
                    fontSize: "12px",
                    color: "gray",
                    textTransform: "capitalize",
                  }}
                >
                  ({rowData.device_name})
                </span>
              )}
            </div>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setEditDeviceId(rowData.device_id);
                setTempDisplayName(rowData.display_name || rowData.device_name || "");
                setIsEditDialogOpen(true);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </div>
        );
      },
    },
    {
      title: "Status",
      field: "status",
      render: (rowData) => {
        let color = "grey";
        if (rowData.status === "APPROVED") color = "#3cb371";
        if (rowData.status === "REVOKED") color = "#d32f2f";
        if (rowData.status === "PENDING") color = "#ed6c02";

        return (
          <span
            style={{
              color: color,
              fontWeight: "600",
              textTransform: "uppercase",
              fontSize: "12px",
            }}
          >
            {rowData.status}
          </span>
        );
      },
    },
    {
      title: "Last Updated Date",
      field: "last_seen",
      render: (rowData) =>
        rowData.last_seen
          ? new Date(rowData.last_seen + " UTC").toLocaleString('en-US', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
            })
          : "Never",
    },
    {
      title: "Actions",
      render: (rowData) => {
        return (
          <div style={{ display: "flex", gap: "8px" }}>
            {rowData.status !== "APPROVED" && (
              <Button
                variant="contained"
                size="small"
                onClick={() => handleApprove(rowData)}
                style={{ backgroundColor: "#3cb371", color: "white" }}
              >
                Approve
              </Button>
            )}
            {rowData.status === "APPROVED" && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => handleRevoke(rowData)}
              >
                Revoke
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div
      style={{
        paddingTop: "96px",
        height: "100vh",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        padding: "96px 20px 20px 20px",
      }}
    >
      <AppBarCommon
        name="Devices"
        showSearch={true}
        searchPlaceholder="Search devices by name..."
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />

      <Dialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Device Name</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter a display name for this device. The original name (provided by
            the device) will be preserved.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Display Name"
            type="text"
            fullWidth
            variant="outlined"
            value={tempDisplayName}
            onChange={(e) => setTempDisplayName(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: "#316C7A",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#316C7A",
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setIsEditDialogOpen(false)}
            sx={{ color: "#316C7A" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveName}
            variant="contained"
            disabled={isLoading}
            sx={{
              backgroundColor: "#316C7A",
              "&:hover": { backgroundColor: "#214b55" },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <div style={{ flex: 1, minHeight: 0, marginTop: "20px" }}>
        <div style={{ overflowX: "auto", height: "100%" }}>
          <MaterialTable
            className="device-table"
            style={{
              position: "relative",
              boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              borderRadius: "12px",
            }}
            title=""
            tableRef={tableRef}
            columns={columns}
            options={{
              debounceInterval: 700,
              padding: "dense",
              toolbar: false,
              pageSize: 10,
              sorting: true,
              thirdSortClick: false,
              headerStyle: {
                fontWeight: 700,
                color: "#316C7A",
                fontSize: "14px",
                height: "3.5rem",
                fontFamily: "Work Sans",
                backgroundColor: "transparent",
                borderBottom: "2px solid #EEE",
              },
              rowStyle: {
                color: "#14285A",
                backgroundColor: "#FFF",
                borderBottom: "2px solid #EEE",
                fontSize: "14px",
                fontFamily: "Work Sans",
              },
              showTitle: false,
              toolbar: false,
              cellStyle: {
                whiteSpace: "nowrap",
                padding: "16px 8px",
              },
              searchFieldStyle: {
                minWidth: "420px",
                paddingRight: "30px",
                borderRadius: "20px",
                padding: "10px 20px",
                boxShadow: "0 0 5px rgba(0, 0, 0, 0.1)",
                width: "360px",
              },
              rowStyle: {
                fontSize: "14px",
              },
            }}
            data={devices}
            onRowClick={handleRowClick}
          />
        </div>
      </div>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: 400, padding: 3, backgroundColor: "#f8f9fa", borderLeft: "1px solid #e0e0e0" }
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <Typography variant="h6" style={{ fontWeight: 600, color: "#316C7A", display: "flex", alignItems: "center", gap: "10px" }}>
             Device Metrics
          </Typography>
          <IconButton onClick={() => setDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </div>

        {selectedDevice && (
          <div style={{ marginBottom: "20px" }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600, color: "#316C7A", textTransform: "capitalize" }}>
              {selectedDevice.display_name || selectedDevice.device_name}
            </Typography>
            <Typography variant="body2" style={{ color: "#666" }}>
              ID: {selectedDevice.device_id}
            </Typography>
          </div>
        )}

        <Divider style={{ marginBottom: "20px" }} />

        {isMetricsLoading ? (
          <div style={{ display: "flex", justifyContent: "center", marginTop: "40px" }}>
            <CircularProgress />
          </div>
        ) : deviceMetrics ? (
          <Box display="flex" flexDirection="column" gap={2}>
            <div style={{ backgroundColor: "white", padding: "15px", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
              <Typography variant="body2" style={{ color: "#777", marginBottom: "4px" }}>Total Events Synced</Typography>
              <Typography variant="h5" style={{ fontWeight: 700, color: "#333" }}>{deviceMetrics.total_events || 0}</Typography>
            </div>
            
            <div style={{ display: "flex", gap: "15px" }}>
              <div style={{ flex: 1, backgroundColor: "white", padding: "15px", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.05)", borderLeft: "4px solid #3cb371" }}>
                <Typography variant="body2" style={{ color: "#777", marginBottom: "4px" }}>Good Workpieces</Typography>
                <Typography variant="h6" style={{ fontWeight: 600, color: "#3cb371" }}>{deviceMetrics.good_events || 0}</Typography>
              </div>
              <div style={{ flex: 1, backgroundColor: "white", padding: "15px", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.05)", borderLeft: "4px solid #d32f2f" }}>
                <Typography variant="body2" style={{ color: "#777", marginBottom: "4px" }}>Defective</Typography>
                <Typography variant="h6" style={{ fontWeight: 600, color: "#d32f2f" }}>{deviceMetrics.defective_events || 0}</Typography>
              </div>
            </div>

            <div style={{ backgroundColor: "white", padding: "15px", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.05)", marginTop: "10px" }}>
              <Typography variant="body2" style={{ color: "#777", marginBottom: "8px", fontWeight: 600 }}>Sync Activity</Typography>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <Typography variant="caption" style={{ color: "#999" }}>First Sync:</Typography>
                <Typography variant="caption" style={{ color: "#333", fontWeight: 500 }}>{deviceMetrics.first_sync_date || "N/A"}</Typography>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" style={{ color: "#999" }}>Last Sync:</Typography>
                <Typography variant="caption" style={{ color: "#333", fontWeight: 500 }}>{deviceMetrics.last_sync_date || "N/A"}</Typography>
              </div>
            </div>
          </Box>
        ) : (
          <Typography variant="body2" style={{ textAlign: "center", marginTop: "20px", color: "#888" }}>
            No metrics available for this device.
          </Typography>
        )}
      </Drawer>

      <Snackbar
        style={{ marginLeft: "100px" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        open={snackbarMessage.message != ""}
        autoHideDuration={2000}
        onClose={() =>
          setSnackBarMessage({ color: "", message: "", severity: "" })
        }
      >
        <Alert
          onClose={() =>
            setSnackBarMessage({ color: "", message: "", severity: "" })
          }
          severity={snackbarMessage.severity}
          sx={{
            width: "100%",
            background: snackbarMessage.color,
            color: "white",
          }}
          style={{ fontFamily: "Work Sans" }}
        >
          {snackbarMessage.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Devices;
